import { useState } from "react";
import { X } from "lucide-react";
import type {
    FinanceBucket,
    PropertyBucket,
    ScoreBreakdown,
} from "@/repo/rank";
import { heatColor } from "@/constants";
import TabList, { type TabData } from "../TabList";
import "./ScorePanel.css";

interface ScorePanelProps {
    score: ScoreBreakdown | null;
    onClose: () => void;
}

type ScoreTab = "location" | "finance" | "property";

const SCORE_TABS: TabData<ScoreTab>[] = [
    { id: "location", label: "Location" },
    { id: "finance", label: "Finance" },
    { id: "property", label: "Property" },
];

/** Flat colour for the property/finance count histograms (the heat scale's hot end). */
const COUNT_BAR_COLOR = heatColor(1);

/** Compact price label, e.g. 250000 -> "£250k", 1500000 -> "£1.5M". */
function formatPrice(value: number): string {
    if (value >= 1_000_000)
        return `£${(value / 1_000_000).toLocaleString(undefined, {
            maximumFractionDigits: 1,
        })}M`;
    if (value >= 1_000)
        return `£${Math.round(value / 1_000).toLocaleString()}k`;
    return `£${Math.round(value).toLocaleString()}`;
}

/** Price-range label for a finance bucket (open-ended at the ends). */
function priceBucketLabel(bucket: FinanceBucket): string {
    if (bucket.minPrice === null)
        return `< ${formatPrice(bucket.maxPrice ?? 0)}`;
    if (bucket.maxPrice === null) return `${formatPrice(bucket.minPrice)}+`;
    return `${formatPrice(bucket.minPrice)}–${formatPrice(bucket.maxPrice)}`;
}

/** Per-band property counts, with the household's own band highlighted. */
function PropertyBuckets(props: {
    buckets: PropertyBucket[];
    householdBand: number | null;
}) {
    const maxCount = Math.max(...props.buckets.map((b) => b.count), 1);
    return (
        <>
            {props.buckets.map((bucket) => {
                const isYou = bucket.bandIndex === props.householdBand;
                const pct = (bucket.count / maxCount) * 100;
                // Bands overlap: each is the stock suitable for a household
                // needing N rooms (the required rooms + a little more).
                const label =
                    bucket.rooms >= 10 ? "needs 10+" : `needs ${bucket.rooms}`;
                return (
                    <div
                        key={bucket.bandIndex}
                        className={
                            "score-panel__bar-row" +
                            (isYou ? " score-panel__bar-row--you" : "")
                        }
                    >
                        <div className="score-panel__bar-head">
                            <span className="score-panel__bar-label">
                                {label}
                                {isYou && (
                                    <span className="score-panel__you-tag">
                                        you
                                    </span>
                                )}
                            </span>
                            <span className="score-panel__bar-value">
                                {bucket.count.toLocaleString()}
                            </span>
                        </div>
                        <div className="score-panel__bar-track">
                            <div
                                className="score-panel__bar-fill"
                                style={{
                                    width: `${pct}%`,
                                    background: COUNT_BAR_COLOR,
                                }}
                            />
                        </div>
                    </div>
                );
            })}
        </>
    );
}

/** Per-price-range property counts, with the budget's bucket highlighted. */
function FinanceBuckets(props: {
    buckets: FinanceBucket[];
    financeBucket: number | null;
}) {
    const maxCount = Math.max(...props.buckets.map((b) => b.count), 1);
    return (
        <>
            {props.buckets.map((bucket, index) => {
                const isYou = index === props.financeBucket;
                const pct = (bucket.count / maxCount) * 100;
                return (
                    <div
                        key={index}
                        className={
                            "score-panel__bar-row" +
                            (isYou ? " score-panel__bar-row--you" : "")
                        }
                    >
                        <div className="score-panel__bar-head">
                            <span className="score-panel__bar-label">
                                {priceBucketLabel(bucket)}
                                {isYou && (
                                    <span className="score-panel__you-tag">
                                        you
                                    </span>
                                )}
                            </span>
                            <span className="score-panel__bar-value">
                                {bucket.count.toLocaleString()}
                            </span>
                        </div>
                        <div className="score-panel__bar-track">
                            <div
                                className="score-panel__bar-fill"
                                style={{
                                    width: `${pct}%`,
                                    background: COUNT_BAR_COLOR,
                                }}
                            />
                        </div>
                    </div>
                );
            })}
        </>
    );
}

function Bar(props: { label: string; value: number }) {
    const pct = Math.max(0, Math.min(1, props.value)) * 100;
    return (
        <div className="score-panel__bar-row">
            <div className="score-panel__bar-head">
                <span className="score-panel__bar-label">{props.label}</span>
                <span className="score-panel__bar-value">
                    {props.value.toFixed(2)}
                </span>
            </div>
            <div className="score-panel__bar-track">
                <div
                    className="score-panel__bar-fill"
                    style={{ width: `${pct}%`, background: heatColor(props.value) }}
                />
            </div>
        </div>
    );
}

function LocationTab({ score }: { score: ScoreBreakdown }) {
    return (
        <div className="score-panel__section">
            <h4>Attributes</h4>
            {score.attributes.length === 0 ? (
                <div className="score-panel__empty">
                    No ranked attributes contributed here.
                </div>
            ) : (
                score.attributes.map((attribute) => (
                    <Bar
                        key={attribute.name}
                        label={attribute.name}
                        value={attribute.value}
                    />
                ))
            )}
        </div>
    );
}

function FinanceTab({ score }: { score: ScoreBreakdown }) {
    return (
        <div className="score-panel__section">
            {score.averagePrice !== null && (
                <div className="score-panel__stat">
                    <span>Average price</span>
                    <span>{formatPrice(score.averagePrice)}</span>
                </div>
            )}
            {score.financeBuckets.length > 0 && (
                <>
                    <h4>Homes by price</h4>
                    <FinanceBuckets
                        buckets={score.financeBuckets}
                        financeBucket={score.financeBucket}
                    />
                </>
            )}
        </div>
    );
}

function PropertyTab({ score }: { score: ScoreBreakdown }) {
    const matchPct =
        score.total && score.total > 0
            ? ((score.matched ?? 0) / score.total) * 100
            : null;

    // A single property (plot) has a yes/no household match rather than an
    // area-wide count: no `total`, but a 1/0 property score.
    const isSingleProperty = score.total === null && score.property !== null;

    return (
        <div className="score-panel__section">
            {isSingleProperty && (
                <div className="score-panel__stat">
                    <span>Matches your household</span>
                    <span>{(score.property ?? 0) >= 1 ? "Yes" : "No"}</span>
                </div>
            )}
            {matchPct !== null && (
                <div className="score-panel__match">
                    <span className="score-panel__match-label">
                        {matchPct.toFixed(matchPct < 10 ? 1 : 0)}% of local
                        homes match your household
                    </span>
                </div>
            )}
            {score.total !== null && (
                <>
                    <div className="score-panel__stat">
                        <span>Properties nearby</span>
                        <span>{score.total.toLocaleString()}</span>
                    </div>
                    <div className="score-panel__stat">
                        <span>Match your household</span>
                        <span>{(score.matched ?? 0).toLocaleString()}</span>
                    </div>
                </>
            )}
            {score.buckets.length > 0 && (
                <>
                    <h4>Suitable homes by household size</h4>
                    <PropertyBuckets
                        buckets={score.buckets}
                        householdBand={score.householdBand}
                    />
                </>
            )}
        </div>
    );
}

/** The three component scores, always visible above the tabs. Each value is
 * coloured on the cool→hot heatmap gradient by its score. */
function ScoreSummary({ score }: { score: ScoreBreakdown }) {
    const items: [string, number | null][] = [
        ["Location", score.location],
        ["Finance", score.finance],
        ["Property", score.property],
    ];
    return (
        <div className="score-panel__summary">
            {items.map(([label, value]) => (
                <div key={label} className="score-panel__summary-item">
                    <div
                        className="score-panel__summary-value"
                        style={{
                            color:
                                value === null ? "#5c626b" : heatColor(value),
                        }}
                    >
                        {value === null ? "—" : value.toFixed(2)}
                    </div>
                    <div className="score-panel__summary-label">{label}</div>
                </div>
            ))}
        </div>
    );
}

export default function ScorePanel({ score, onClose }: ScorePanelProps) {
    const [tab, setTab] = useState<ScoreTab>("location");
    if (!score) return null;

    return (
        <div className="score-panel">
            <div className="score-panel__header">
                <div className="score-panel__title">
                    <div className="score-panel__name">{score.name}</div>
                    <div className="score-panel__score">
                        {score.value.toFixed(2)}
                    </div>
                </div>
                <button
                    className="score-panel__close"
                    onClick={onClose}
                    aria-label="Close"
                >
                    <X size={18} />
                </button>
            </div>

            {!score.explained ? (
                <div className="score-panel__hint">
                    Turn on <strong>Explain on hover</strong> in the Rank panel
                    to see the full breakdown.
                </div>
            ) : (
                <>
                    <ScoreSummary score={score} />
                    <TabList
                        tabs={SCORE_TABS}
                        current={tab}
                        setCurrent={setTab}
                    />
                    {tab === "location" && <LocationTab score={score} />}
                    {tab === "finance" && <FinanceTab score={score} />}
                    {tab === "property" && <PropertyTab score={score} />}
                </>
            )}
        </div>
    );
}
